"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import {
  Users,
  Award,
  Calendar,
  Vote,
  Shirt,
  BarChart3,
  FileText,
  Plus,
  ChevronDown,
  Image,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
  section: "turnaj" | "reprezentácia" | "hlasovanie" | "info";
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

// MegaMenu.tsx
const MENU_CONTAINER_CLASSES = cn(
  "absolute top-full mt-2 left-1/2 transform -translate-x-1/2 min-w-[16rem] max-w-md",
  "bg-background border border-border shadow-2xl z-50 rounded-b-xl",
  "animate-in fade-in slide-in-from-top-2 duration-200",
);

const MENU_SECTIONS = [
  {
    title: "Turnaj",
    icon: Award,
    items: [
      { nazov: "Skupiny", href: "/skupiny", ikona: Users },
      { nazov: "Hlavná tabuľka", href: "/tabulka", ikona: BarChart3 },
      { nazov: "Pavúk", href: "/pavuk", ikona: Award },
      { nazov: "Zápasy", href: "/zapasy", ikona: Award },
      { nazov: "Strelci", href: "/strelci", ikona: Award },
      { nazov: "Kalendár", href: "/kalendar-zapasov", ikona: Calendar },
    ],
  },
  {
    title: "Reprezentácia",
    icon: Award,
    items: [
      { nazov: "Reprezentácia", href: "/slovensko", ikona: Award },
      { nazov: "Navrhnúť hráča", href: "/navrh-hraca", ikona: Plus },
    ],
  },
  {
    title: "Hlasovanie",
    icon: Vote,
    items: [
      { nazov: "Hlasovanie", href: "/hlasovanie", ikona: Vote },
      { nazov: "Výsledky hlasovania", href: "/vysledky", ikona: BarChart3 },
      { nazov: "Galéria nominácií", href: "/galeria", ikona: Image },
    ],
  },
  {
    title: "Info",
    icon: FileText,
    items: [
      { nazov: "Dresy", href: "/dresy", ikona: Shirt },
      { nazov: "Štatistiky IHF", href: "/statistiky-ihf", ikona: BarChart3 },
      { nazov: "Pravidlá", href: "/pravidla", ikona: FileText },
    ],
  },
];

export default function MegaMenu({
  isOpen,
  onClose,
  isMobile = false,
  section,
  onMouseEnter,
  onMouseLeave,
}: MegaMenuProps) {
  const pathname = usePathname();

  const selectedSection = MENU_SECTIONS.find(
    (s) => s.title.toLowerCase() === section,
  );

  if (!isOpen) return null;

  if (isMobile && selectedSection) {
    return (
      <div className="w-full">
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <selectedSection.icon className="w-5 h-5 text-primary" />
              {selectedSection.title}
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-2">
            {selectedSection.items.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg transition-colors",
                  "hover:bg-muted/50 group",
                  pathname === item.href
                    ? "bg-muted text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <item.ikona className="w-4 h-4 shrink-0 group-hover:text-primary transition-colors" />
                <span className="truncate">{item.nazov}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const content = selectedSection ? (
    <div className="min-w-0">
      <h3 className="text-xl font-semibold text-foreground flex items-center gap-2 mb-4">
        <selectedSection.icon className="w-6 h-6 text-primary shrink-0" />
        <span className="truncate">{selectedSection.title}</span>
      </h3>
      <div className="space-y-3">
        {selectedSection.items.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            onClick={onClose}
            className={cn(
              "flex items-center gap-4 p-4 rounded-lg transition-colors",
              "hover:bg-muted/50 group",
              pathname === item.href
                ? "bg-primary/10 text-primary font-medium"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <item.ikona className="w-5 h-5 shrink-0 group-hover:text-primary transition-colors" />
            <span className="truncate text-lg">{item.nazov}</span>
          </Link>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div
      className={MENU_CONTAINER_CLASSES}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="max-h-[80vh] overflow-y-auto">
        <div className="w-full px-6 py-8">
          <div className="max-w-6xl mx-auto">{content}</div>
        </div>
      </div>
    </div>
  );
}
